import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import Filters from "components/Common/Filters"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import DownloadCSV from "components/Shared/DownloadCSV"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { getWalletPurchaseReports } from "store/actions/reportsAction"
import { useTranslation } from "react-i18next"
import isPermissionEnabled from "common/isPermissionEnabled"

const formatDateTime = date =>
  date
    .toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "")

const getDefaultDateRange = () => {
  const today = new Date()
  const startOfDay = new Date(today)
  startOfDay.setHours(0, 0, 0, 0)

  const endOfDay = new Date(today)
  endOfDay.setHours(23, 59, 0, 0)

  return {
    min: formatDateTime(startOfDay),
    max: formatDateTime(endOfDay),
  }
}

const WalletPurchaseReport = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Wallet Purchase Report ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
  })

  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    walletPurchaseReport,
  } = useSelector(state => state.Reports)

  const [selectedFilters, setSelectedFilters] = useState({
    branchId: [],
    groupBy: "",
    countryId: [],
    regionId: [],
    cityId: [],
    customerId: [],
  })
  const { userRole, accountType } = useSelector(state => state.Login)

  const [dateRange, setDateRange] = useState(() => getDefaultDateRange())
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const flatpickrRef = useRef(null)
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      16,
      "view_wallet_purchase_reports",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
    })
  }, [userRole])

  useEffect(() => {
    fetchWalletPurchaseReports()
  }, [])

  const fetchWalletPurchaseReports = () => {
    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      customerId: [],
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }
    dispatch(getWalletPurchaseReports(payload))
    setIsApiCalled(true)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const filters = []

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  const getColumns = () => {
    return [
      "Date",
      "Sender Name",
      "Sender Phone Number",
      "Receiver Name",
      "Receiver Phone Number",
      "Amount",
      "Payment Method",
      "Payment Status",
      "Transaction Id",

    ]
  }

  const columns = getColumns()

  const renderCell = (item, column) => {
    switch (column) {
      case "Date":
        return item?.createdAt
          ? new Date(item.createdAt).toISOString().split("T")[0]
          : ""
      case "Sender Name":
        return item?.sender?.name
      case "Sender Phone Number":
        return (
          <span dir="ltr" style={{ unicodeBidi: "bidi-override" }}>
            {item?.sender?.phone}
          </span>
        )
      case "Receiver Name":
        return item?.receiver?.name
      case "Receiver Phone Number":
        return (
          <span dir="ltr" style={{ unicodeBidi: "bidi-override" }}>
            {item?.receiver?.phone}
          </span>
        )
      case "Amount":
        return item.amount < 0 ? 0 : formatNumber(item.amount)
      case "Payment Method":
        return item?.transactionDetails?.paymentMethod?.charAt(0).toUpperCase() + item?.transactionDetails?.paymentMethod?.slice(1).toLowerCase() || "-"
      case "Payment Status":
        return item?.transactionDetails?.gatewayStatus ?? "-"
      case "Transaction Id":
        return item?.transactionDetails?.transactionId ?? "-"
      default:
        return item[column] || ""
    }
  }
  const formatNumber = number => {
    return Number(number)?.toFixed(2)
  }

  const handleSearch = () => {
    const { customerId } = selectedFilters
    const customerIds = customerId?.map(item => item.value) || []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      customerId: customerIds,
      keyword: searchTerm,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }

    dispatch(getWalletPurchaseReports(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/wallet-purchase-reports?page=${1}`)
        }
      })
      .catch(error => {})
  }

  const handleReset = () => {
    const defaultDateRange = getDefaultDateRange()
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      customerId: [],
      dateRange: defaultDateRange,
      keyword: "",
    }
    setSearchTerm("")
    dispatch(getWalletPurchaseReports(payload))
    navigate(`/wallet-purchase-reports?page=${1}`)
    setSelectedFilters({
      branchId: [],
      groupBy: "",
      cityId: [],
      regionId: [],
      countryId: [],
      customerId: [],
    })
    setDateRange(defaultDateRange)
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate(
        [defaultDateRange.min, defaultDateRange.max],
        true
      )
    }
  }

  const onPageChange = page => {
    const { customerId } = selectedFilters
    const customerIds = customerId?.map(item => item.value) || []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      customerId: customerIds,
      keyword: searchTerm,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }

    dispatch(getWalletPurchaseReports(payload))
    navigate(`/wallet-purchase-reports?page=${page}`)
  }

  const exportToCSV = async () => {
    const { customerId } = selectedFilters
    const customerIds = customerId?.map(item => item.value) || []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "REPORT-PURCHASE-WALLET",
      exportType: "csv",
      customerId: customerIds,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }

    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      const fileName = hasDateRange
        ? t(`wallet_purchase_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Wallet Purchase Report Till Date")
      DownloadCSV(csvData, fileName)
    }
  }

  const handleExcelDownload = async () => {
    const { customerId } = selectedFilters
    const customerIds = customerId?.map(item => item.value) || []

    const hasDateRange = dateRange?.min && dateRange?.max
    const payload = {
      type: "REPORT-PURCHASE-WALLET",
      exportType: "xlsx",
      customerId: customerIds,
      dateRange: hasDateRange ? { min: dateRange.min, max: dateRange.max } : {},
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      const fileName = hasDateRange
        ? t(`wallet_purchase_report_from_${dateRange.min}_to_${dateRange.max}`)
        : t("Wallet Purchase Report Till Date")
      DownloadXLSX(xlsxData, fileName)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                cardTitle={t("Wallet Purchase Reports")}
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                isExportData={loading ? false : true}
              />
              <CardBody>
                <Filters
                  dateRangeWithTime={true}
                  filters={filters}
                  hideSearch={false}
                  searchTerm={searchTerm}
                  searchBy={t("Name, Phone Number")}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  handleReset={handleReset}
                  selectedFilters={selectedFilters}
                  isRangeDate={true}
                  dateRange={dateRange}
                  flatpickrRef={flatpickrRef}
                  handleSearchChange={handleSearchChange}
                  setDateRange={setDateRange}
                  colSize={4}
                />
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : walletPurchaseReport?.length === 0 ? (
                    <p className="text-center my-5 py-3">
                      {t("No data found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          {columns?.map((column, index) => (
                            <th key={index}>{t(column)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {walletPurchaseReport?.map((item, index) => (
                          <tr key={index}>
                            {columns?.map((column, colIndex) => (
                              <td key={colIndex}>
                                {renderCell(item, column) ?? "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>

                {!loading && walletPurchaseReport?.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={walletPurchaseReport}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}
export default WalletPurchaseReport